<div class="page-wrapper">

  <div class="page-header">
    <div class="page-title">
      <svg class="page-title__icon" width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <path fill-rule="evenodd" clip-rule="evenodd" d="M3 2a1 1 0 00-1 1v11h12V3a1 1 0 00-1-1H3Zm2 3h2v2H5V5Zm4 0h2v2H9V5ZM5 9h2v2H5V9Zm4 0h2v2H9V9Z" fill="currentColor"/>
      </svg>
      <h1 class="page-title__text">Organizations</h1>
    </div>
    <p class="page-title__sub">Every tenant on the platform.</p>
  </div>

  @if (totals; as t) {
    <app-admin-totals-card
      [description]="hasFilters ? 'The organizations matching your filters.' : 'Every organization on the platform.'"
      [filterLabel]="totalsFilterLabel"
      [columns]="4"
      [loading]="isLoading"
    >
      <div appTotalsFigure label="Organizations" info="How many organizations match the current filters. The note splits out billing-exempt and FOCUS ones." [note]="t.billingExempt + ' billing-exempt · ' + t.focus + ' FOCUS'">{{ t.organizations | number }}</div>
      <div appTotalsFigure label="Total balance" info="The combined wallet balance of these organizations right now." note="Across their wallets">{{ t.balanceCents | money }} {{ t.currency | uppercase }}</div>
      <div appTotalsFigure label="Negative balances" info="How many of these organizations have a wallet balance below zero right now."
           [tone]="t.negativeBalances > 0 ? 'danger' : 'default'"
           [note]="t.negativeBalances > 0 ? 'Wallets below zero' : 'Every wallet is at or above zero'">{{ t.negativeBalances | number }}</div>
      <div appTotalsFigure label="Billed" info="Organizations charged for their API usage. Billing-exempt organizations aren't charged, but their usage is still tracked." [note]="t.billingExempt + ' exempt'" [progress]="t.organizations ? t.billed / t.organizations : 0">{{ t.billed | number }}</div>
    </app-admin-totals-card>
  } @else if (!hasLoadedOnce) {
    <app-admin-totals-card [skeletonLabels]="['Organizations', 'Total balance', 'Negative balances', 'Billed']" [columns]="4" />
  }

  <div class="api-card api-card--full" #listCard>
    <div class="admin-filter-bar">
      <div class="admin-filter-bar__search">
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><circle cx="7.25" cy="7.25" r="4.75" stroke="currentColor" stroke-width="1.6"/><path d="M10.75 10.75 14 14" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>
        <input type="text" placeholder="Search organizations..." [ngModel]="search" (ngModelChange)="onSearchChange($event)" />
      </div>
      @if (phone) {
        <!-- Phones: the other filters live in a sheet (admin-filters-sheet). -->
        <button type="button" class="admin-filter-bar__filters" (click)="openFilters()">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M2.5 4h11M4.5 8h7M6.5 12h3" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>
          Filters
          @if (activeFilterCount) {
            <span class="admin-filter-bar__count">{{ activeFilterCount }}</span>
          }
        </button>
      } @else {
        <select class="admin-filter-bar__select" [ngModel]="exemptOnly ? 'exempt' : 'all'" (ngModelChange)="onExemptChange($event)">
          <option value="all">All billing</option>
          <option value="exempt">Exempt only</option>
        </select>
        <select class="admin-filter-bar__select" [ngModel]="focusOnly ? 'focus' : 'all'" (ngModelChange)="onFocusChange($event)">
          <option value="all">All organizations</option>
          <option value="focus">FOCUS only</option>
        </select>
      }
    </div>

    @if (isLoading) {
      <div class="admin-table-scroll">
        <table class="billing-table admin-stack admin-stack--orgs">
          <thead>
            <tr>
              <th>Organization</th>
              <th>Billing</th>
              <th>FOCUS</th>
              <th class="col-num">Balance</th>
              <th>Created</th>
            </tr>
          </thead>
          <tbody>
            @for (i of [1,2,3,4,5,6]; track i) {
              <tr>
                <td><app-skeleton width="65%" height="14px"></app-skeleton></td>
                <td><app-skeleton width="60px" height="20px" radius="4px"></app-skeleton></td>
                <td><app-skeleton width="30px" height="14px"></app-skeleton></td>
                <td><app-skeleton width="70px" height="14px"></app-skeleton></td>
                <td><app-skeleton width="80px" height="14px"></app-skeleton></td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    } @else if (rows.length) {
      <div class="admin-table-scroll">
        <table class="billing-table admin-stack admin-stack--orgs">
          <thead>
            <tr>
              <th>Organization</th>
              <th>Billing</th>
              <th>FOCUS</th>
              <th class="col-num">Balance</th>
              <th>Created</th>
            </tr>
          </thead>
          <tbody>
            @for (row of rows; track row.id) {
              <tr class="admin-row--link" [routerLink]="['/admin/organizations', row.slug]">
                <td>{{ row.name }}<br><span class="billing-table__mono">{{ row.slug }}</span></td>
                <td>
                  @if (row.billingExempt) {
                    <span class="txn-kind-badge txn-kind-badge--neutral">Exempt</span>
                  } @else {
                    Billed
                  }
                </td>
                <td [class.cell-status--muted]="!row.focus">{{ row.focus ? 'Yes' : 'No' }}</td>
                <td class="col-num" [class.billing-card__balance--negative]="row.balanceCents < 0">
                  {{ row.balanceCents | money }} {{ row.currency | uppercase }}
                </td>
                <td>{{ row.createdAt | date:'mediumDate' }}</td>
              </tr>
            }
          </tbody>
        </table>
      </div>

      <div class="table-footer">
        <span>{{ pagination?.total ?? rows.length | number }} organization{{ (pagination?.total ?? rows.length) === 1 ? '' : 's' }}</span>
        @if (pagination && pagination.pages > 1) {
          <div class="table-footer__pager">
            <button type="button" class="btn--secondary" [disabled]="page <= 1" (click)="goToPage(page - 1)">Previous</button>
            <span>Page {{ page }} of {{ pagination.pages }}</span>
            <button type="button" class="btn--secondary" [disabled]="page >= pagination.pages" (click)="goToPage(page + 1)">Next</button>
          </div>
        }
      </div>
    } @else {
      <div class="empty-state">
        @if (hasFilters) {
          <p class="empty-state__title">No organizations match your filters.</p>
          <p class="empty-state__desc">Try a different name or slug, or clear the filters.</p>
          <button type="button" class="btn--secondary empty-state__action" (click)="clearFilters()">Clear filters</button>
        } @else {
          <p class="empty-state__title">No organizations yet.</p>
          <p class="empty-state__desc">Every tenant that signs up appears here.</p>
        }
      </div>
    }
  </div>

</div>
